import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Menu, MenuItem, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Label,
} from '@lightricks/react-design-system';
import elevationShadow from '@/utils/elevationShadow';
import AnimatedCaret from '@/components/animated-caret';
import Checkbox from '@/components/checkbox';
import Divider from '@/components/divider';
import {scrollbarMixins} from '@/styles/mixins';
import styles from './Dropdown.module.scss';

const shouldNotForwardProps = ['menuWidth', 'menuMaxHeight', 'hideScrollbar'];

type StyledMenuProps = {
  menuWidth: number;
  menuMaxHeight?: number;
  hideScrollbar?: boolean;
};

const StyledMenu = styled(Menu, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<StyledMenuProps>(({menuWidth, menuMaxHeight, hideScrollbar}) => ({
  '& .MuiList-root': {
    padding: 0,
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    boxShadow: elevationShadow(2),
    minWidth: `${menuWidth}px`,
    ...(menuMaxHeight && {
      maxHeight: `${menuMaxHeight}px`,
      overflowY: 'auto',
    }),
    ...(hideScrollbar
      ? scrollbarMixins.overrideScrollbarHidden()
      : scrollbarMixins.overrideScrollbar()),
  },
  '& .MuiMenu-list': {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'disableSelectedBackground',
})<{disableSelectedBackground?: boolean}>(({disableSelectedBackground}) => ({
  'padding': '12px 16px',
  '&:hover': {
    background: designSystemToken('semantic.bg.tertiary'),
  },
  '&.Mui-selected': {
    'backgroundColor': disableSelectedBackground
      ? 'transparent'
      : designSystemToken('semantic.bg.inverse-secondary'),
    '&:hover': {
      background: designSystemToken('semantic.bg.tertiary'),
    },
  },
}));

export type DropdownBadgeStyle = {
  backgroundColor?: string;
  textColor?: string;
};

export type DropdownOption = {
  id: string;
  label: string;
  subtitle?: string;
  badge?: string;
  badgeStyle?: DropdownBadgeStyle;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  /**
   * When true, renders a divider line ABOVE this option to visually separate
   * it from the previous group. Useful for grouping menu items by category
   * (e.g. state-changing actions vs. read-only actions) without needing
   * dividers between every single item. Independent of `showItemDividers`.
   */
  separatorBefore?: boolean;
};

export type DropdownSelectionMode = 'single' | 'multi' | 'multiCheckboxes';

export type DropdownProps = {
  /** Text shown on the trigger button. */
  label: string;
  /** Menu header title. */
  title?: string;
  /** Subtitle shown below the title. */
  subtitle?: string;
  /** Options to display in the menu. */
  options: DropdownOption[];
  /** Currently selected option id(s). Pass a single-element array for single-select. */
  selectedIds: string[];
  /** Called when an option is toggled. */
  onChange: (option: DropdownOption) => void;
  /** Selection behavior. @default 'single' */
  selectionMode?: DropdownSelectionMode;
  /** Icon element rendered before the label in the trigger button. */
  icon?: React.ReactNode;
  /** Additional class name for the trigger button. */
  className?: string;
  /** Minimum width of the menu panel (px). @default 320 */
  menuWidth?: number;
  /** Maximum height of the menu panel (px). */
  menuMaxHeight?: number;
  /** Custom trigger element. Receives onClick and isOpen for state management. */
  renderTrigger?: (props: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    isOpen: boolean;
  }) => React.ReactNode;
  /** Content shown when options is empty. */
  emptyState?: React.ReactNode;
  /** When true, hides the scrollbar. @default false */
  hideMenuScrollbar?: boolean;
  /** When true, scrolls to the first selected item on open. @default false */
  shouldScrollToSelectedItem?: boolean;
  /** When true, the menu width matches the trigger element width. @default false */
  matchTriggerWidth?: boolean;
  /** When true, shows dividers between items. @default true */
  showItemDividers?: boolean;
  /** Horizontal position of the menu relative to the trigger. @default 'center' */
  menuPositionHorizontal?: 'left' | 'center' | 'right';
  /** When true, stops click propagation from menu items. @default false */
  shouldStopEventPropagation?: boolean;
  /** Called when the dropdown menu opens. */
  onOpen?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Called when the dropdown menu closes. */
  onClose?: (event: React.SyntheticEvent | Event) => void;
  /** When true, selected items have no background highlight. @default false */
  disableSelectedBackground?: boolean;
};

function Dropdown({
  label,
  title,
  subtitle,
  options,
  selectedIds,
  onChange,
  selectionMode = 'single',
  icon,
  className,
  menuWidth = 320,
  menuMaxHeight,
  renderTrigger,
  emptyState,
  hideMenuScrollbar = false,
  shouldScrollToSelectedItem = false,
  matchTriggerWidth = false,
  showItemDividers = true,
  menuPositionHorizontal = 'center',
  shouldStopEventPropagation = false,
  onOpen,
  onClose,
  disableSelectedBackground = false,
}: DropdownProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const selectedItemRef = useRef<HTMLLIElement | null>(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleClose = useCallback((event: React.SyntheticEvent | Event) => {
    setAnchorEl(null);
    onCloseRef.current?.(event);
  }, []);

  const handleTriggerClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const willOpen = !isOpen;
      setAnchorEl(event.currentTarget);
      if (willOpen) {
        onOpenRef.current?.(event);
      }
    },
    [isOpen]
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const isSingle = selectionMode === 'single';

  const canScrollToSelected = shouldScrollToSelectedItem && isSingle;

  const defaultTrigger = (
    <Button
      className={className}
      variant="text"
      size="small"
      startIcon={icon}
      endIcon={
        <AnimatedCaret
          isOpen={isOpen}
          color={designSystemToken('semantic.fg.inverse-secondary')}
          size={18}
        />
      }
      onClick={handleTriggerClick}
    >
      {label}
    </Button>
  );

  return (
    <>
      {renderTrigger
        ? renderTrigger({onClick: handleTriggerClick, isOpen})
        : defaultTrigger}
      <StyledMenu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        menuWidth={matchTriggerWidth ? 0 : menuWidth}
        menuMaxHeight={menuMaxHeight}
        hideScrollbar={hideMenuScrollbar}
        MenuListProps={{variant: 'selectedMenu'}}
        {...(matchTriggerWidth &&
          anchorEl && {
            slotProps: {
              paper: {
                style: {width: anchorEl.offsetWidth},
              },
            },
          })}
        {...(canScrollToSelected && {
          TransitionProps: {
            onEntering: () => {
              selectedItemRef.current?.scrollIntoView?.({
                block: 'center',
                behavior: 'instant',
              });
            },
          },
        })}
        transformOrigin={{vertical: -8, horizontal: menuPositionHorizontal}}
        anchorOrigin={{vertical: 'bottom', horizontal: menuPositionHorizontal}}
      >
        {title ? (
          <div className={styles.menuTitleContainer}>
            <div className={styles.menuTitle}>
              <Headline
                size="xxs"
                color={designSystemToken('semantic.fg.inverse-secondary')}
              >
                {title}
              </Headline>
              {subtitle ? (
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.inverse-secondary')}
                >
                  {subtitle}
                </Body>
              ) : null}
            </div>
            <Divider
              margin={0}
              color={designSystemToken('semantic.bg.alpha')}
            />
          </div>
        ) : null}
        {options.length === 0 && emptyState ? emptyState : null}
        {options.map((option, index) => {
          const isSelected = selectedSet.has(option.id);
          const showDivider = index < options.length - 1 && showItemDividers;
          // Render a group separator ABOVE this item when `separatorBefore` is
          // set — but skip it for the first option, since there's nothing to
          // separate from at the top of the menu.
          const showSeparatorBefore = index > 0 && option.separatorBefore;
          return (
            <div key={option.id}>
              {showSeparatorBefore ? (
                <Divider
                  margin={0}
                  color={designSystemToken('semantic.bg.alpha')}
                />
              ) : null}
              <StyledMenuItem
                disableSelectedBackground={disableSelectedBackground}
                ref={
                  canScrollToSelected && isSelected
                    ? selectedItemRef
                    : undefined
                }
                onClick={(event) => {
                  if (shouldStopEventPropagation) {
                    event.stopPropagation();
                  }
                  onChange(option);
                  if (isSingle) {
                    handleClose(event);
                  }
                }}
                selected={isSelected}
                disabled={option.disabled}
                disableRipple
              >
                <div className={styles.menuItem}>
                  <div className={styles.menuItemLeft}>
                    {selectionMode === 'multiCheckboxes' ? (
                      <div className={styles.checkboxWrapper}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {}}
                          shouldStopPropagation={false}
                        />
                      </div>
                    ) : null}
                    {option.startIcon ? (
                      <div className={styles.optionIcon}>
                        {option.startIcon}
                      </div>
                    ) : null}
                    <div className={styles.menuItemText}>
                      <div className={styles.menuItemLabelRow}>
                        <Label
                          size="md"
                          color={designSystemToken('semantic.fg.primary')}
                        >
                          {option.label}
                        </Label>
                        {option.badge ? (
                          <Body
                            size="xs"
                            className={styles.badge}
                            style={{
                              backgroundColor:
                                option.badgeStyle?.backgroundColor ??
                                designSystemToken('semantic.bg.tertiary'),
                              color:
                                option.badgeStyle?.textColor ??
                                designSystemToken('semantic.fg.primary'),
                              border: `1px solid ${designSystemToken('semantic.bg.alpha')}`,
                            }}
                          >
                            {option.badge}
                          </Body>
                        ) : null}
                      </div>
                      {option.subtitle ? (
                        <Body
                          size="sm"
                          color={designSystemToken(
                            'semantic.fg.inverse-secondary'
                          )}
                        >
                          {option.subtitle}
                        </Body>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.menuItemRight}>
                    {option.endIcon ? (
                      <div className={styles.optionIcon}>{option.endIcon}</div>
                    ) : null}
                  </div>
                </div>
              </StyledMenuItem>
              {showDivider ? (
                <Divider
                  margin={0}
                  color={designSystemToken('semantic.bg.alpha')}
                />
              ) : null}
            </div>
          );
        })}
      </StyledMenu>
    </>
  );
}

export default Dropdown;
